/**
 * ConsentItemsBuilder Component
 *
 * Dynamic list builder for consent checkbox items.
 * Each item is a RichTextEditor row with add/remove controls.
 * Data model: string[] — each element is one consent checkbox's HTML content.
 */

import React, { useCallback, useMemo } from 'react';
import { RichTextEditor } from '@/components/common/RichTextEditor';

import './ConsentItemsBuilder.scss';

interface ConsentItemsBuilderProps {
  value: string[] | undefined;
  onChange: (items: string[] | undefined) => void;
}

export const ConsentItemsBuilder: React.FC<ConsentItemsBuilderProps> = ({ value, onChange }) => {
  const items = useMemo(() => value || [], [value]);

  const handleAdd = useCallback(() => {
    onChange([...items, '']);
  }, [items, onChange]);

  const handleRemove = useCallback(
    (index: number) => {
      const updated = items.filter((_, i) => i !== index);
      onChange(updated.length > 0 ? updated : undefined);
    },
    [items, onChange]
  );

  const handleUpdate = useCallback(
    (index: number, html: string) => {
      const updated = items.map((item, i) => (i === index ? html : item));
      onChange(updated);
    },
    [items, onChange]
  );

  return (
    <div className="consent-items-builder">
      <div className="consent-items-builder__header">
        <h4>Consent Checkboxes</h4>
        <p className="consent-items-builder__description">
          Each item becomes a checkbox the user must check before proceeding. Supports rich text (bold, links, etc.).
        </p>
      </div>

      {items.length === 0 ? (
        <div className="consent-items-builder__empty">
          <p>No consent items configured. Users will not see any checkboxes.</p>
        </div>
      ) : (
        <div className="consent-items-builder__list">
          {items.map((html, index) => (
            <div key={index} className="consent-items-builder__item">
              <div className="consent-items-builder__item-header">
                <span className="consent-items-builder__item-number">Checkbox {index + 1}</span>
                <button
                  type="button"
                  className="consent-items-builder__remove-btn"
                  onClick={() => handleRemove(index)}
                  title="Remove this consent item"
                >
                  ×
                </button>
              </div>
              <RichTextEditor
                value={html}
                onChange={(newHtml) => handleUpdate(index, newHtml)}
                placeholder="e.g., I acknowledge that my data will be shared..."
              />
            </div>
          ))}
        </div>
      )}

      <button type="button" className="consent-items-builder__add-btn" onClick={handleAdd}>
        + Add Consent Item
      </button>
    </div>
  );
};

export default ConsentItemsBuilder;
